import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';

interface RoadmapView {
  readonly roadmapId: string;
  readonly roadmapCode: string;
  readonly roadmapName: string;
  readonly description: string | null;
  readonly roadmapType: string;
  readonly status: string;
  readonly horizonStart: string | null;
  readonly horizonEnd: string | null;
  readonly items: unknown;
  readonly versionNo: number;
}

interface RoadmapItem {
  readonly label: string;
  readonly detail: string | null;
}

/**
 * Normalise the persisted roadmap items (jsonb) into a display list. A roadmap
 * (T442) stores its release-plan items as a jsonb array; each entry may be a
 * plain string or an object carrying the planned story / milestone (§M L33.05
 * release_plan_item.story) under one of several shapes.
 */
function parseItems(raw: unknown): RoadmapItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    if (typeof entry === 'string') return { label: entry, detail: null };
    if (entry && typeof entry === 'object') {
      const o = entry as Record<string, unknown>;
      const label = o.story ?? o.title ?? o.name ?? o.label ?? o.item;
      const detail = o.description ?? o.detail ?? o.target ?? o.horizon ?? o.status;
      return {
        label: typeof label === 'string' ? label : JSON.stringify(entry),
        detail: typeof detail === 'string' ? detail : null,
      };
    }
    return { label: String(entry), detail: null };
  });
}

/**
 * GR_M31-M35 §L33.05 / §M — roadmap (release plan) detail viewer
 * (`/admin/pm/roadmaps/:id`). Renders a single roadmap (T442) with its planning
 * horizon and the ordered release-plan items / stories it carries (§M L33.05
 * release_plan_item.story → roadmap.items). Read-only; roadmap authoring stays
 * on the roadmap planner.
 */
export function RoadmapDetailPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [roadmap, setRoadmap] = useState<RoadmapView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const items = useMemo(() => (roadmap ? parseItems(roadmap.items) : []), [roadmap]);

  useEffect(() => {
    let alive = true;
    if (!id) { setError(t('m33.roadmapDetail.noId')); setLoading(false); return; }
    setLoading(true);
    apiRequest<{ data: RoadmapView }>(`/v1/pm/roadmaps/${id}`, { token })
      .then((r) => { if (alive) { setRoadmap(r.data); setError(null); } })
      .catch((e) => { if (alive) setError(e instanceof ApiError ? e.message : t('m33.roadmapDetail.loadFailed')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, id, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m33.common.notAuthorised', { perm })
      : (err as Error).message;

  const act = async (path: string, label: string, perm: string): Promise<void> => {
    if (!id) return;
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/pm/roadmaps/${id}/${path}`, { method: 'POST', body: {}, token });
      setNotice(t('m33.common.noticeLabel', { label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, perm));
    }
  };

  if (loading) return <main aria-label="roadmap-detail"><p>{t('m33.common.loading')}</p></main>;
  if (error) return <main aria-label="roadmap-detail"><p role="alert">{error}</p></main>;
  if (!roadmap) return <main aria-label="roadmap-detail"><p role="alert">{t('m33.roadmapDetail.notFound')}</p></main>;

  return (
    <main aria-label="roadmap-detail">
      <h1>{roadmap.roadmapName}</h1>
      <dl>
        <dt>{t('m33.common.code')}</dt><dd aria-label="roadmap-code">{roadmap.roadmapCode}</dd>
        <dt>{t('m33.common.type')}</dt><dd aria-label="roadmap-type">{roadmap.roadmapType}</dd>
        <dt>{t('m33.common.status')}</dt><dd aria-label="roadmap-status">{roadmap.status}</dd>
        <dt>{t('m33.roadmapDetail.horizonStart')}</dt><dd aria-label="roadmap-horizon-start">{roadmap.horizonStart ?? '—'}</dd>
        <dt>{t('m33.roadmapDetail.horizonEnd')}</dt><dd aria-label="roadmap-horizon-end">{roadmap.horizonEnd ?? '—'}</dd>
        <dt>{t('m33.common.version')}</dt><dd aria-label="roadmap-version">{roadmap.versionNo}</dd>
      </dl>

      {roadmap.description && <p aria-label="roadmap-description">{roadmap.description}</p>}

      <section aria-label="roadmap-lifecycle">
        <h2>{t('m33.roadmapDetail.lifecycle')}</h2>
        {roadmap.status === 'DRAFT' && (
          <Button variant="secondary" type="button" aria-label="submit-roadmap" onClick={() => void act('submit', t('m33.roadmapDetail.act.submitted'), 'ROADMAP_MANAGE')}>{t('m33.roadmapDetail.btnSubmit')}</Button>
        )}
        {roadmap.status === 'PENDING_APPROVAL' && (
          <Button variant="secondary" type="button" aria-label="approve-roadmap" onClick={() => void act('approve', t('m33.roadmapDetail.act.approved'), 'ROADMAP_APPROVE')}>{t('m33.roadmapDetail.btnApprove')}</Button>
        )}
        {roadmap.status === 'APPROVED' && (
          <Button variant="primary" type="button" aria-label="publish-roadmap" onClick={() => void act('publish', t('m33.roadmapDetail.act.published'), 'ROADMAP_PUBLISH')}>{t('m33.roadmapDetail.btnPublish')}</Button>
        )}
        {roadmap.status !== 'ARCHIVED' && (
          <Button variant="danger" type="button" aria-label="archive-roadmap" onClick={() => void act('archive', t('m33.roadmapDetail.act.archived'), 'ROADMAP_MANAGE')}>{t('m33.roadmapDetail.btnArchive')}</Button>
        )}
        {actionError && <p role="alert" aria-label="roadmap-action-error">{actionError}</p>}
        {notice && <p aria-label="roadmap-action-notice">{notice}</p>}
      </section>

      <section aria-label="roadmap-items">
        <h2>{t('m33.roadmapDetail.itemsHeading')}</h2>
        {items.length > 0 ? (
          <ol aria-label="roadmap-item-list">
            {items.map((it, i) => (
              <li key={`item-${i}`} aria-label={`roadmap-item-${i}`}>
                {it.label}{it.detail ? ` — ${it.detail}` : ''}
              </li>
            ))}
          </ol>
        ) : (
          <p aria-label="roadmap-no-items">{t('m33.roadmapDetail.noItems')}</p>
        )}
      </section>
    </main>
  );
}
